import {ApiOutlined, BellOutlined, CloseCircleOutlined, MailOutlined, RightOutlined, RobotOutlined, WarningOutlined} from '@ant-design/icons';
import {Badge, Button, Dropdown, Empty, List, Spin, Tag, Typography} from 'antd';
import React, {useState} from 'react';
import {useDataProvider, useQueryWithStore} from 'react-admin';
import {Link} from 'react-router-dom';
import {ExtendedDataProvider, NotificationItem, NotificationType} from '@src/types';
import {formatDateAndTimeFromNowWithTooltip} from '@src/helpers/utils';
import useGetGeneralSettings from '../../providers/useGetGeneralSettings';
import {SecureMarkdownRenderer} from '../../ui/SecureMarkdownRenderer';
import {getActiveMembership} from '../Common/utils';
import {getReadMessageIds, markMessagesRead} from './readMessages';
import './NotificationInbox.css';

const notificationTypes: {
    [type in NotificationType]: { icon: JSX.Element, label: string, color: string, link: (item: NotificationItem) => string | null }
} = {
    message: {
        icon: <MailOutlined style={{ color: '#1890ff', fontSize: 18, marginTop: 4 }} />,
        label: 'Message',
        color: 'blue',
        link: () => null,
    },
    change_instance: {
        icon: <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18, marginTop: 4 }} />,
        label: 'Change Instance',
        color: 'red',
        // The item id is the ChangeInstance id. Named in the hash, the list
        // pins it first and expands it (useLinkedPin) — and the API returns
        // it even though the default PENDING,APPROVED filter would hide a
        // REJECTED/ERROR change.
        link: (item) => `/change_instances#${item.id}`,
    },
    ai_processor_response: {
        icon: <RobotOutlined style={{ color: '#ff4d4f', fontSize: 18, marginTop: 4 }} />,
        label: 'AI Processor',
        color: 'red',
        // The pipeline detail tab of the service item the failed response
        // belongs to; pipeline_version selects the failed pipeline run.
        link: (item) => item.service_item_id
            ? `/owner_service_items/${item.service_item_id}?tab=netorca_pack${item.pipeline_version ? `&pipeline_version=${item.pipeline_version}` : ''}`
            : '/serviceowner_ai_processors',
    },
    webhook_response: {
        icon: <ApiOutlined style={{ color: '#ff4d4f', fontSize: 18, marginTop: 4 }} />,
        label: 'Webhook',
        color: 'red',
        link: () => '/webhooks?tab=responses',
    },
};

const NotificationInbox = () => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [open, setOpen] = useState(false);
    // GET is read-only server-side: events are listed since the active
    // membership's last_seen_notifications anchor, which only moves when
    // the user actually views them (POST mark_seen on dropdown open).
    const [seen, setSeen] = useState(false);
    const teamId = getActiveMembership()?.team?.id ?? null;
    // Messages this browser has already shown. Read once per mount so opening
    // the bell does not make the list vanish under the reader mid-glance — it
    // takes effect on the next fetch, the same way the server anchor does.
    const [readMessages] = useState(() => getReadMessageIds(teamId));

    const { data, total, loaded } = useQueryWithStore({
        type: "getList",
        resource: "notifications",
        payload: {
            pagination: { page: 1, perPage: 20 },
            sort: { field: "modified", order: "DESC" },
            filter: {}
        },
    });

    const generalSettings = useGetGeneralSettings();
    const bannerSettings = generalSettings.find((s: any) => s.name === 'GlobalBanner');
    const bannerEnabled = bannerSettings?.config?.ENABLED;
    const bannerMessage = bannerSettings?.config?.message;

    /* What the server sent, less the messages this browser has already shown.
       Events need no such filtering: the server has already applied the
       membership's anchor to them, so anything still listed is genuinely
       outstanding. */
    const served: NotificationItem[] = data || [];
    const notifications = served.filter(
        (item) => !(item.type === 'message' && readMessages.has(item.id)),
    );

    // The banner is not counted: it is ambient information, not an unread
    // notification, and would leave a permanent warning badge on the bell.
    // The count is of what is actually shown, so a suppressed message does not
    // leave a badge over an inbox that has nothing new in it.
    const badgeCount = seen ? 0 : notifications.length;

    /**
     * The newest notification we were actually served — never "now".
     *
     * The list is fetched on mount while the acknowledgement happens later, so
     * anything that fails in between has to survive to the next poll:
     * anchoring on now would mark it seen without it ever being shown, and
     * nothing re-touches `modified`, so it would never come back. Taking the
     * max rather than the first item keeps this right whatever order the store
     * hands the records back in; messages carry no date and so are never the
     * anchor. Nothing dated means there is nothing to acknowledge.
     */
    const newestSeen = () => notifications.reduce<string | null>(
        (newest, item) => (item.modified && (!newest || item.modified > newest) ? item.modified : newest),
        null,
    );

    /**
     * Move the membership's anchor. This is the whole of the read-tracking
     * mechanism and it lives on the server (`orcaexternal` NotificationView),
     * keyed to the active membership — so it follows the user between browsers
     * and keeps a separate position per team, and the next GET is filtered
     * against it rather than the client having to hide what it already has.
     */
    const markSeen = (until: string) =>
        dataProvider.createAction('notifications', { verb: 'mark_seen/', data: { until } })
            .catch((error: any) => { console.error(error); });

    /**
     * Opening the bell is the acknowledgement — there is no button to press.
     * Both halves happen here: the server anchor moves for the events, and the
     * messages on screen are recorded locally, since nothing on the server can
     * describe a message as seen.
     */
    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen && !seen) {
            setSeen(true);

            // Everything still listed is passed as the prune set, so ids for
            // messages that have expired or been deleted stop being kept.
            markMessagesRead(
                teamId,
                notifications.filter((item) => item.type === 'message').map((item) => item.id),
                served.filter((item) => item.type === 'message').map((item) => item.id),
            );

            const until = newestSeen();
            if (!until) return;
            // Persist the anchor so the listed events don't reappear after a
            // reload; the already-fetched list stays visible this session.
            markSeen(until);
        }
    };

    const renderNotification = (item: NotificationItem) => {
        const config = notificationTypes[item.type] || notificationTypes.message;
        const link = config.link(item);
        const meta = (
            <List.Item.Meta
                avatar={config.icon}
                title={
                    /* the kind, the name and the time on one wrapping row — the
                       time used to be floated, which laid it over the name as
                       soon as the panel was narrow enough for the name to wrap */
                    <span className="nti__meta">
                        <Tag color={config.color}>{config.label}</Tag>
                        <span className="nti__name">
                            {item.name}{item.type === 'change_instance' && ` #${item.id}`}
                        </span>
                        {item.modified && (
                            <Typography.Text type="secondary" className="nti__when">
                                {formatDateAndTimeFromNowWithTooltip(item.modified)}
                            </Typography.Text>
                        )}
                    </span>
                }
                description={<SecureMarkdownRenderer className="notification-inbox-markdown" content={item.message} />}
            />
        );
        return (
            <List.Item className={link ? 'notification-inbox-item-link' : undefined}>
                {link ? (
                    <Link to={link} onClick={() => setOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', color: 'inherit' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>{meta}</div>
                        <RightOutlined className="nti__chev" />
                    </Link>
                ) : meta}
            </List.Item>
        );
    };

    // the banner is ambient and always stays — it is not a notification and
    // was never counted as one
    const listed = notifications;
    const showBanner = Boolean(bannerEnabled && bannerMessage);

    const notificationContent = (
        <div className="nti">
            <div className="nti__head">
                <span className="nti__title">Notifications</span>
                {listed.length > 0 && <span className="nti__count">{listed.length}</span>}
            </div>
            <div className="nti__body">
                {!loaded ? (
                    <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
                ) : listed.length === 0 && !showBanner ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No notifications"
                        style={{ padding: 24 }}
                    />
                ) : (
                    <List
                        dataSource={[
                            ...(showBanner ? [{ _isBanner: true, message: bannerMessage }] : []),
                            ...listed,
                        ]}
                        renderItem={(item: any) => (
                            item._isBanner ? (
                                <List.Item className="nti__banner">
                                    <List.Item.Meta
                                        avatar={<WarningOutlined style={{ color: '#faad14', fontSize: 18, marginTop: 4 }} />}
                                        description={<SecureMarkdownRenderer className="notification-inbox-markdown" content={item.message} />}
                                    />
                                </List.Item>
                            ) : renderNotification(item)
                        )}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Dropdown open={open} onOpenChange={handleOpenChange} dropdownRender={() => notificationContent} trigger={['click']} placement="bottomRight">
            <Badge count={badgeCount} size="small" offset={[-2, 2]}>
                {/* Sized in the stylesheet rather than here: the bar it sits on
                    can change colour underneath it, and a style written inline
                    is the one thing that cannot be answered from CSS. */}
                <Button
                    className="topbar-bell"
                    type="text"
                    icon={<BellOutlined />}
                />
            </Badge>
        </Dropdown>
    );
};

export default NotificationInbox;
